import {
  Button,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { KYDPromoCode, KYDPromoCodeMode } from "@common/interfaces/KYDCart";
import { EventDetailPromocodesSection } from "@common/interfaces/KYDEvent";
import { FaCheckCircle } from "react-icons/fa";
import { getRandomString } from "../../../utils";
import { Controller, useForm } from "react-hook-form";

export type PromocodesValue = {
  id?: string;
  code: string;
  enabled: boolean;
  status: string;
  amount: string;
  mode: KYDPromoCodeMode;
  ticket_type_ids: string[];
  max_total_uses: string | undefined;
  max_uses_per_user: string | undefined;
};

const EditPromocodeModal = ({
  data,
  promoCode,
  onClose,
  onSave,
  isLoading = false,
  isOpen = false,
  currency,
}: {
  isOpen?: boolean;
  data: EventDetailPromocodesSection["data"];
  currency: string;
  isLoading: boolean;
  onClose: () => void;
  promoCode: KYDPromoCode | null;
  onSave: (promoCode: PromocodesValue) => void;
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    formState: {},
  } = useForm<PromocodesValue>({
    criteriaMode: "all",
    reValidateMode: "onBlur",
    mode: "all",
    values: {
      id: promoCode?.id ? promoCode.id : undefined,
      code: promoCode?.code ? promoCode.code : "",
      max_total_uses: promoCode?.max_total_uses
        ? promoCode?.max_total_uses.toString()
        : undefined,
      max_uses_per_user: promoCode?.max_uses_per_user
        ? promoCode.max_uses_per_user.toString()
        : undefined,
      enabled: promoCode?.status === "active" ? true : false,
      amount: promoCode?.amount ? (promoCode.amount / 100).toString() : "0",
      mode: promoCode?.mode ? promoCode.mode : "percent",
      status: promoCode?.mode ? promoCode.mode : "active",
      ticket_type_ids: promoCode?.ticket_type_ids
        ? promoCode?.ticket_type_ids
        : [],
    },
  });
  const enabled = watch("enabled");
  const status = watch("status");
  const mode = watch("mode");
  const maxTotalUses = watch("max_total_uses");
  const maxUsesPerUser = watch("max_uses_per_user");
  const isEmptyOrWhitespace = (value: string | undefined) =>
    value === undefined || value.trim() === "";

  const isInvalidNumber = (value: string | undefined) =>
    !isEmptyOrWhitespace(value) && (isNaN(Number(value)) || Number(value) <= 0);

  const hasInvalidMaxUses =
    isInvalidNumber(maxTotalUses) || isInvalidNumber(maxUsesPerUser);
  const isSaveDisabled = hasInvalidMaxUses;

  const _onClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      size={"xl"}
      isOpen={isOpen}
      onClose={_onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {promoCode ? "Edit Promo Code" : "Create Promo Code"}
        </ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            <HStack>
              <Stack spacing={0}>
                <Text fontWeight={"medium"}>Code</Text>
                <InputGroup size={"md"}>
                  <Input
                    pr="6rem"
                    isDisabled={isLoading}
                    {...register("code")}
                  />
                  {!promoCode ? (
                    <InputRightElement width="6rem">
                      <Button
                        onClick={() => {
                          const s = getRandomString(10).toUpperCase();
                          setValue("code", s, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                        h="1.75rem"
                        size="sm"
                      >
                        Generate
                      </Button>
                    </InputRightElement>
                  ) : null}
                </InputGroup>
              </Stack>
              <Stack spacing={0} flexGrow={1} marginLeft={2}>
                <Text fontWeight={"medium"}>Enabled</Text>
                <HStack>
                  <Switch
                    isChecked={enabled}
                    checked={enabled}
                    isDisabled={status === "expired"}
                    defaultChecked={enabled}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        setValue("status", "active", {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        setValue("enabled", true);
                        return;
                      }
                      setValue("status", "disabled", {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setValue("enabled", false);
                    }}
                    size={"lg"}
                    colorScheme={"green"}
                  />
                  <Text>{enabled ? "Yes" : "No"}</Text>
                </HStack>
              </Stack>
            </HStack>

            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Discount</Text>
              <HStack>
                <Select {...register("mode")} maxW="200px">
                  <option value={"percent"}>Percent Off</option>
                  <option value={"fixed"}>Fixed Amount</option>
                </Select>
                <InputGroup>
                  <Input
                    type={"number"}
                    isDisabled={isLoading}
                    {...register("amount")}
                  />
                  <InputRightAddon
                    children={
                      mode === "percent"
                        ? "%"
                        : mode === "fixed"
                          ? currency
                          : "%"
                    }
                  />
                </InputGroup>
              </HStack>
            </Stack>

            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Ticket Types</Text>
              <Controller
                control={control}
                defaultValue={[]}
                name="ticket_type_ids"
                render={({ field }) => (
                  <Stack
                    rounded={"lg"}
                    borderWidth={"1px"}
                    borderColor={"gray.300"}
                    p={2}
                    maxH={"300px"}
                    overflowY={"scroll"}
                  >
                    {data.ticket_types
                      .concat(data.tiered_ticket_types)
                      .map((tt) => {
                        const prevValue = field.value?.find(
                          (selected) => selected === tt.id
                        );

                        return (
                          <HStack
                            justify={"space-between"}
                            h="50px"
                            p={2}
                            rounded={"lg"}
                            borderWidth={"1px"}
                            key={`${tt.id}-${field.value?.length}`}
                            cursor={"pointer"}
                            onClick={() => {
                              const otherValues =
                                field.value?.filter(
                                  (selectedTt) => selectedTt !== tt.id
                                ) || [];

                              setValue(
                                "ticket_type_ids",
                                otherValues.concat(prevValue ? [] : tt.id)
                              );
                            }}
                          >
                            <Text fontWeight={prevValue ? "bold" : "normal"}>
                              {tt.name}
                            </Text>
                            <Icon
                              display={prevValue ? "inherit" : "none"}
                              boxSize={8}
                              color={"green.500"}
                              as={FaCheckCircle}
                            />
                          </HStack>
                        );
                      })}
                  </Stack>
                )}
              />
            </Stack>
            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Max Number Of Uses</Text>
              <Input
                placeholder="Optional"
                type={"number"}
                {...register("max_total_uses")}
              />
            </Stack>
            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Max Number Of Uses Per User</Text>
              <Input
                placeholder="Optional"
                type={"number"}
                {...register("max_uses_per_user")}
                isDisabled={isLoading}
              />
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Stack spacing={1} alignItems="flex-start">
            {hasInvalidMaxUses ? (
              <Text color="orange.500" fontSize="sm" paddingRight="16px">
                Max usage limits must be greater than zero or left blank.
              </Text>
            ) : null}
          </Stack>
          <Button isDisabled={isLoading} onClick={_onClose} mr="4">
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            _hover={{
              backgroundColor: "green.600",
            }}
            isDisabled={isLoading || isSaveDisabled}
            isLoading={isLoading}
            onClick={handleSubmit(onSave)}
          >
            {promoCode ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditPromocodeModal;
